import { z } from 'zod';
import { ProtocolType, VehicleCategory, FuelType, isElectric } from './enums';

/** Antwortmoeglichkeiten wie auf dem Papierformular (JA / NEIN / nicht relevant). */
export const CheckAnswer = { JA: 'JA', NEIN: 'NEIN', NICHT_RELEVANT: 'NICHT_RELEVANT' } as const;
export type CheckAnswer = (typeof CheckAnswer)[keyof typeof CheckAnswer];
export const checkAnswerSchema = z.nativeEnum(CheckAnswer);

export const CHECK_ANSWER_LABEL: Record<CheckAnswer, string> = {
  JA: 'Ja',
  NEIN: 'Nein',
  NICHT_RELEVANT: 'nicht relevant',
};

export interface ChecklistItem {
  key: string;
  label: string;
  /** Ist "nicht relevant" zulaessig? Entspricht der 3. Spalte im Papierformular. */
  allowNotRelevant: boolean;
  /** Nur anzeigen, wenn dieses Item mit JA beantwortet wurde. */
  dependsOn?: { key: string; value: CheckAnswer };
  /** Nur fuer diese Fahrzeugklassen anzeigen. */
  onlyCategories?: VehicleCategory[];
  /** Nur fuer E-/Hybridfahrzeuge anzeigen. */
  onlyElectric?: boolean;
  /** Bei dieser Antwort ist eine Bemerkung Pflicht (z. B. fehlender Schluessel). */
  requireNoteOn?: CheckAnswer;
  hint?: string;
}

/**
 * Checkliste 1:1 aus "Rueckgabecheckliste PKW.docx" / "Uebergabeprotokoll PKW.docx" /
 * "Uebergabeprotokoll LCV.docx" - identische 15 Positionen in identischer Reihenfolge.
 */
export const CHECKLIST_ITEMS: ChecklistItem[] = [
  {
    key: 'fahrzeugschluessel',
    label: 'Fahrzeugschlüssel (2 Schlüssel)',
    allowNotRelevant: false,
    requireNoteOn: CheckAnswer.NEIN,
    hint: 'Bei "Nein" bitte Anzahl und Verbleib notieren – am Leasingende kostenpflichtig.',
  },
  {
    key: 'zusatzschluessel_standheizung',
    label: 'Zusätzlicher Schlüssel (Standheizung)',
    allowNotRelevant: true,
  },
  {
    key: 'zulassungsbescheinigung_1',
    label: 'Zulassungsbescheinigung Teil I (Kfz-Schein)',
    allowNotRelevant: false,
    requireNoteOn: CheckAnswer.NEIN,
  },
  { key: 'hu_au', label: 'Hauptuntersuchung (HU/AU)', allowNotRelevant: false },
  { key: 'tank_ladekarte', label: 'Tank-/Ladekarte', allowNotRelevant: false },
  {
    key: 'zweiter_reifensatz',
    label: '2. Reifensatz (Sommer-/Winterräder)',
    allowNotRelevant: false,
    hint: 'Lagerort angeben, falls eingelagert.',
  },
  {
    key: 'bordmappe',
    label: 'Bordmappe (Betriebsanleitung, Serviceheft, etc.)',
    allowNotRelevant: false,
  },
  {
    key: 'netztrennwand_laderaumabdeckung',
    label: 'Netztrennwand / Laderaumabdeckung',
    allowNotRelevant: false,
  },
  { key: 'verbandskasten', label: 'Verbandskasten', allowNotRelevant: false },
  { key: 'warnwesten', label: 'Warnwesten', allowNotRelevant: false },
  { key: 'fahrtenbuch', label: 'Fahrtenbuch', allowNotRelevant: false },
  {
    key: 'elektronisches_fahrtenbuch',
    label: 'Elektronisches Fahrtenbuch (alt_Gegenbauer)',
    allowNotRelevant: true,
  },
  {
    key: 'ausbau_beauftragt',
    label: 'Ausbau beauftragt',
    allowNotRelevant: true,
    dependsOn: { key: 'elektronisches_fahrtenbuch', value: CheckAnswer.JA },
    hint: 'Nur relevant, wenn ein elektronisches Fahrtenbuch verbaut ist.',
  },
  {
    key: 'ladekabel_e_fahrzeug',
    label: 'Ladekabel E-Fahrzeug',
    allowNotRelevant: true,
    onlyElectric: true,
  },
  { key: 'umweltplakette', label: 'Umweltplakette', allowNotRelevant: false },
];

/** Zusatzpositionen, die es nur auf dem LCV-Formular sinnvoll gibt. */
export const CHECKLIST_ITEMS_LCV_EXTRA: ChecklistItem[] = [
  {
    key: 'laderaum_zustand',
    label: 'Laderaum / Zurrösen / Regalsystem vollständig',
    allowNotRelevant: true,
    onlyCategories: [VehicleCategory.LCV],
  },
  {
    key: 'anhaengerkupplung',
    label: 'Anhängerkupplung inkl. Adapter',
    allowNotRelevant: true,
    onlyCategories: [VehicleCategory.LCV],
  },
];

/**
 * Liefert die tatsaechlich anzuzeigende Checkliste fuer ein konkretes Fahrzeug.
 * E-spezifische und LCV-spezifische Positionen werden ein-/ausgeblendet.
 */
export function checklistFor(category: VehicleCategory, fuel: FuelType): ChecklistItem[] {
  const all = [...CHECKLIST_ITEMS, ...CHECKLIST_ITEMS_LCV_EXTRA];
  return all.filter((item) => {
    if (item.onlyElectric && !isElectric(fuel)) return false;
    if (item.onlyCategories && !item.onlyCategories.includes(category)) return false;
    return true;
  });
}

/** Bei welchen Vorgangsarten wird die Ausstattungs-Checkliste ueberhaupt erhoben? */
export const TYPES_WITH_CHECKLIST: ProtocolType[] = [
  ProtocolType.UEBERGABE,
  ProtocolType.RUECKGABE,
];

/**
 * Kurz-Check fuer die wiederkehrende Zustandsdokumentation ("Check-In").
 * Bewusst knapp gehalten - 1-2x pro Woche, muss in unter 2 Minuten machbar sein.
 */
export interface ConditionCheckItem {
  key: string;
  label: string;
  hint?: string;
}

export const CONDITION_CHECK_ITEMS: ConditionCheckItem[] = [
  { key: 'reifen_profil_druck', label: 'Reifen: Profil & Druck in Ordnung' },
  { key: 'beleuchtung', label: 'Beleuchtung rundum funktionsfähig' },
  { key: 'fluessigkeiten', label: 'Öl / Kühlwasser / Wischwasser ausreichend' },
  { key: 'scheiben_wischer', label: 'Scheiben & Wischerblätter ohne Mängel' },
  { key: 'warnleuchten', label: 'Keine Warnleuchten im Kombiinstrument' },
  { key: 'sauberkeit_innen', label: 'Innenraum sauber & geruchsfrei' },
  { key: 'sauberkeit_aussen', label: 'Außen sauber (Schäden erkennbar)' },
  { key: 'verbandskasten_warndreieck', label: 'Verbandskasten & Warndreieck vorhanden, Ablaufdatum ok' },
  { key: 'hu_faellig', label: 'HU/AU nicht überfällig' },
  { key: 'ladekabel_vorhanden', label: 'Ladekabel vorhanden (nur E-Fahrzeug)' },
];

export const checklistAnswerRecordSchema = z.record(
  z.string(),
  z.object({ value: checkAnswerSchema, note: z.string().max(500).optional() })
);
export type ChecklistAnswerRecord = z.infer<typeof checklistAnswerRecordSchema>;
